import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Package, FileImage, Gift, Bell } from "lucide-react";
import Layout from "../../components/Layout/Layout";

const TABS = [
    { key: "all", labelKey: "COMMON_ALL" },
    { key: "orders", labelKey: "SIDEBAR_GIFT_ORDERS" },
    { key: "content", labelKey: "SIDEBAR_MY_CONTENT" },
    { key: "gifts", labelKey: "SIDEBAR_MY_GIFTS" },
];

const MOCK_NOTIFICATIONS = [
    {
        id: 1,
        type: "orders",
        icon: Package,
        title: "Order #1234 confirmed",
        message: "Your gift order has been confirmed and is being processed.",
        date: "Today, 10:32 AM",
        read: false,
    },
    {
        id: 2,
        type: "content",
        icon: FileImage,
        title: "Content approved",
        message: "Your submitted post has been reviewed and approved.",
        date: "Today, 09:15 AM",
        read: false,
    },
    {
        id: 3,
        type: "gifts",
        icon: Gift,
        title: "New gift available",
        message: "You have reached a new tier. A new gift is ready for you to claim.",
        date: "Yesterday, 03:45 PM",
        read: false,
    },
    {
        id: 4,
        type: "orders",
        icon: Package,
        title: "Order #1198 delivered",
        message: "Your order has been delivered to the selected address.",
        date: "Yesterday, 11:00 AM",
        read: true,
    },
    {
        id: 5,
        type: "content",
        icon: FileImage,
        title: "Content pending review",
        message: "Your recently submitted content is under review by the KORRES team.",
        date: "2 days ago",
        read: true,
    },
    {
        id: 6,
        type: "gifts",
        icon: Gift,
        title: "Points updated",
        message: "Your points balance has been updated following your latest order.",
        date: "3 days ago",
        read: true,
    },
];

const NotificationItem = ({ notification, onMarkRead }) => {
    const { icon: Icon, title, message, date, read } = notification;

    return (
        <button
            type="button"
            onClick={() => !read && onMarkRead(notification.id)}
            className={`w-full flex items-start gap-4 rounded-2xl px-4 py-4 text-left transition ${
                read ? "bg-[#f5f6fb]" : "bg-white border border-primary/10 shadow-sm"
            }`}
        >
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${
                read ? "bg-slate-100" : "bg-primary/10"
            }`}>
                <Icon className={`h-5 w-5 ${read ? "text-slate-400" : "text-primary"}`} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm font-semibold truncate ${read ? "text-slate-500" : "text-slate-900"}`}>
                        {title}
                    </p>
                    {!read && (
                        <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                    )}
                </div>
                <p className="mt-0.5 text-xs text-slate-500 leading-relaxed">{message}</p>
                <p className="mt-1 text-xs text-slate-400">{date}</p>
            </div>
        </button>
    );
};

const Notifications = () => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab] = useState("all");
    const [notifications, setNotifications] = useState(MOCK_NOTIFICATIONS);

    const filtered = activeTab === "all"
        ? notifications
        : notifications.filter((n) => n.type === activeTab);

    const unreadCount = notifications.filter((n) => !n.read).length;

    const handleMarkRead = (id) => {
        setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, read: true } : n));
    };

    const handleMarkAllRead = () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    };

    return (
        <Layout activeLabel="Notifications">
            <div>
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-lg font-semibold text-slate-900">
                            {t("NOTIFICATIONS_TITLE")}
                            {unreadCount > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-white">
                                    {unreadCount}
                                </span>
                            )}
                        </h1>
                        <p className="mt-1 text-sm text-slate-500">{t("NOTIFICATIONS_SUBTITLE")}</p>
                    </div>
                    {unreadCount > 0 && (
                        <button
                            type="button"
                            onClick={handleMarkAllRead}
                            className="text-xs font-semibold text-primary underline underline-offset-4 hover:opacity-75 focus:outline-none"
                        >
                            {t("NOTIFICATIONS_MARK_ALL_READ")}
                        </button>
                    )}
                </div>

                <div className="mt-6 flex gap-2 overflow-x-auto pb-1">
                    {TABS.map((tab) => {
                        const tabUnread = tab.key === "all"
                            ? unreadCount
                            : notifications.filter((n) => n.type === tab.key && !n.read).length;

                        return (
                            <button
                                key={tab.key}
                                type="button"
                                onClick={() => setActiveTab(tab.key)}
                                className={`flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition focus:outline-none ${
                                    activeTab === tab.key
                                        ? "bg-primary text-white"
                                        : "bg-[#f5f6fb] text-slate-500 hover:bg-slate-200"
                                }`}
                            >
                                {t(tab.labelKey)}
                                {tabUnread > 0 && (
                                    <span className={`inline-flex items-center justify-center rounded-full px-1.5 text-xs font-bold ${
                                        activeTab === tab.key ? "bg-white text-primary" : "bg-primary text-white"
                                    }`}>
                                        {tabUnread}
                                    </span>
                                )}
                            </button>
                        );
                    })}
                </div>

                <div className="mt-4 space-y-3">
                    {filtered.length > 0 ? (
                        filtered.map((n) => (
                            <NotificationItem
                                key={n.id}
                                notification={n}
                                onMarkRead={handleMarkRead}
                            />
                        ))
                    ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                            <Bell className="h-10 w-10 mb-3 text-slate-300" />
                            <p className="text-sm">{t("NOTIFICATIONS_EMPTY")}</p>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Notifications;
